
import { useState, useCallback, useEffect } from 'react';
import { Order, ThemeMode } from '../types';
import { useStrategyQueue, StrategySuggestion } from './useStrategyQueue';

const DISPATCHER_THEME_KEY = 'sal_theme_dispatcher';
const DRIVER_THEME_KEY = 'sal_theme_driver';

const isThemeMode = (value: string | null): value is ThemeMode => value === 'dark' || value === 'solar';

const getStoredTheme = (key: string, fallback: ThemeMode): ThemeMode => {
  if (typeof window === 'undefined') return fallback;
  const value = localStorage.getItem(key);
  return isThemeMode(value) ? value : fallback;
};

const saveTheme = (key: string, mode: ThemeMode): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(key, mode);
};

export const useSalUI = () => {
  const [dispatcherTheme, setDispatcherThemeState] = useState<ThemeMode>(() => getStoredTheme(DISPATCHER_THEME_KEY, 'dark'));
  const [driverTheme, setDriverThemeState] = useState<ThemeMode>(() => getStoredTheme(DRIVER_THEME_KEY, 'dark'));

  // --- VISUAL STATE ---
  const [viewMode, setViewMode] = useState<'triage' | 'map' | 'analytics'>('triage');
  const [cardViewMode, setCardViewMode] = useState<'standard' | 'compact'>('standard');
  
  // Auto-switch to compact mode on smaller screens (< 1440px)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia('(max-width: 1439px)');
    
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      // Only auto-switch if user hasn't manually toggled
      const hasManualOverride = sessionStorage.getItem('sal_cardview_manual');
      if (!hasManualOverride) {
        setCardViewMode(e.matches ? 'compact' : 'standard');
      }
    };
    
    // Initial check
    handleChange(mediaQuery);
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  const handleSetCardViewMode = useCallback((mode: 'standard' | 'compact') => {
    // Mark as manually overridden when user explicitly changes
    sessionStorage.setItem('sal_cardview_manual', 'true');
    setCardViewMode(mode);
  }, []);
  
  const [isSimpleView, setIsSimpleView] = useState(false);
  const [focusedOrderId, setFocusedOrderId] = useState<string | null>(null);
  const [isCommandBarOpen, setIsCommandBarOpen] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false); 
  const [mapDisplayMode, setMapDisplayMode] = useState<'tactical' | 'recon'>('tactical');

  // --- MODAL STATE ---
  const [modals, setModals] = useState({
    settings: false,
    openDelivery: false,
    staffPin: false,
    paste: false,
    edit: false,
    incident: false,
    cashFlow: false,
    // v46.4: Modal de Decisão Sofia para estratégias divergentes
    sofiaDecision: {
        isOpen: false,
        mode: 'decision' as 'decision' | 'justification',
        strategyType: 'standard' as any,
        orders: [] as Order[],
        savings: 0,
        delayImpact: 0,
        description: '',
        onConfirm: () => {}
    },
    centralAlert: { isOpen: false, title: '', message: '', icon: 'info' as any, actions: [] as any[] }
  });

  const [activeModalOrder, setActiveModalOrder] = useState<Order | null>(null);

  // v59.1: Fila de sugestões de estratégias para gerenciamento sequencial
  const strategyQueue = useStrategyQueue();

  const setDispatcherTheme = useCallback((mode: ThemeMode) => {
    setDispatcherThemeState(mode);
    saveTheme(DISPATCHER_THEME_KEY, mode);
  }, []);

  const setDriverTheme = useCallback((mode: ThemeMode) => {
    setDriverThemeState(mode);
    saveTheme(DRIVER_THEME_KEY, mode);
  }, []);

  const toggleDispatcherTheme = useCallback(() => {
    setDispatcherTheme(dispatcherTheme === 'solar' ? 'dark' : 'solar');
  }, [dispatcherTheme, setDispatcherTheme]);

  const toggleDriverTheme = useCallback(() => {
    setDriverTheme(driverTheme === 'solar' ? 'dark' : 'solar');
  }, [driverTheme, setDriverTheme]);

  // --- ACTIONS ---
  const toggleModal = useCallback((name: keyof typeof modals, value?: boolean) => {
      setModals(prev => ({ ...prev, [name]: value !== undefined ? value : !prev[name] }));
  }, []);

  const openSofiaDecision = useCallback((data: any) => {
      setModals(prev => ({ 
          ...prev, 
          sofiaDecision: { ...prev.sofiaDecision, ...data, isOpen: true } 
      }));
  }, []);

  const closeSofiaDecision = useCallback(() => {
      setModals(prev => ({ 
          ...prev, 
          sofiaDecision: {
              ...prev.sofiaDecision,
              isOpen: false,
              mode: 'decision',
              onConfirm: () => {}
          }
      }));
  }, []);

  const openEditModal = useCallback((order: Order) => {
      setActiveModalOrder(order);
      toggleModal('edit', true);
  }, [toggleModal]);

  const openIncidentModal = useCallback((order: Order) => {
      setActiveModalOrder(order);
      toggleModal('incident', true);
  }, [toggleModal]);

  const closeAllModals = useCallback(() => {
      setModals(prev => {
          const closed = { ...prev };
          Object.keys(closed).forEach(k => {
              if (k !== 'centralAlert' && k !== 'sofiaDecision') (closed as any)[k] = false;
          });
          return closed;
      });
      setActiveModalOrder(null);
  }, []);

  return {
    dispatcherTheme,
    setDispatcherTheme,
    toggleDispatcherTheme,
    driverTheme,
    setDriverTheme,
    toggleDriverTheme,
    viewMode, setViewMode,
    cardViewMode, setCardViewMode: handleSetCardViewMode,
    isSimpleView, setIsSimpleView,
    focusedOrderId, setFocusedOrderId,
    isCommandBarOpen, setIsCommandBarOpen,
    isSimulating, setIsSimulating,
    mapDisplayMode, setMapDisplayMode,
    modals, setModals,
    activeModalOrder, setActiveModalOrder,
    toggleModal,
    openSofiaDecision,
    closeSofiaDecision,
    openEditModal,
    openIncidentModal,
    closeAllModals,
    // v59.1: Fila de sugestões de estratégias
    strategyQueue
  };
};
